import { FC, useEffect, useRef, useState } from 'react';
import { IQueryParamDrivenDrawerContentProvider } from '../../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { useSubscription } from '@meraki-internal/state';
import { QueryParamDrivenDrawerPage } from '../../components/CareMapDrawerPage';
import { BlockUntilActiveCaremap } from '../../ActiveCareMapProvider';
import { AddFromPopover } from '../../contacts/AddFromPopover';
import { IonButton, IonSpinner } from '@ionic/react';
import { isPlatform } from '../../utils/isPlatform';
import { Text } from '../../components/Text';
import { createUseStyles } from 'react-jss';
import { AddFromContactsButton, IAddFromContactsButtonResult } from '../../contacts/AddFromContactsButton';
import { ShareNoteState } from './ShareNoteState';
import { TextField } from '../../components/TextField';
import { EmailField, IEmailContactMethod } from '../../contact-methods/EmailField';
import { CareMapState } from '../../caremap/CareMapState';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { EmailValidator } from '../../utils/EmailValidator';
import { ICareMap, IContactMethod } from '../../caremap/ICareMap';
import { Checkbox } from '../../components/Checkbox';
import { AlertPresenter } from '../../AlertBinder';
import { MAX_HOUSEHOLD_MEMBERS } from '../../nodes/NodeAdapter';
import { useScrollIntoView } from '../../utils/useScrollIntoView';
import { formatPhoneNumber } from '../../utils/format';
import { useAutoFocus } from '../../utils/useAutoFocus';
import { ViewShareLinkButton } from '../../share-caremap/ViewShareLinkButton';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';

const useStyles = createUseStyles({
    upperSection: {
        gap: 16,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center'
    }
});

interface IShareNoteDrawerProps {
    noteId: string;
    //trackingFrom?: string;
    setValidity: (valid: boolean) => void;
};

export class ShareNoteWithQueryParamDrawer implements IQueryParamDrivenDrawerContentProvider<IShareNoteDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IShareNoteDrawerProps>){}

    mode = 'share-note';

    Component = ShareNoteDrawerView;

    options = {
        allowBackdropClose: true
    };

    open = (props: IShareNoteDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };

    getUrl = (props: IShareNoteDrawerProps) => {
        return this.drawerHelper.getUrl({ mode: this.mode, ...props });
    };
};

export const ShareNoteDrawerContent: FC<IShareNoteDrawerProps> = ({ noteId, setValidity/* , trackingFrom */ }) => {
    const classes = useStyles();

    const careMapState = useInstance(CareMapState);
    //const tracker = useInstance(CareMapNoteTracker);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const shareNoteState = useInstance(ShareNoteState);
    useSubscription(() => shareNoteState);

    const [maxHouseholdMembersMet, setMaxHouseholdMembersMet] = useState(false);
    const [showAddContactPopover, setShowAddContactPopover] = useState(false);

    const lastRowRef = useRef<HTMLDivElement>(null);
    const addFromContactsRef = useRef<HTMLIonButtonElement>(null);
    const firstNameRef = useRef<HTMLIonInputElement>(null);

    const focusFirstName = useAutoFocus(firstNameRef);
    const scrollIntoView = useScrollIntoView();

    const model = notesFactory.forActiveCaremap();
    const note = model.getNote(noteId);

    const caremap = careMapState.getActiveCaremap() as ICareMap;
    const hasCreateViewerAccessTokenLink = Boolean(caremap.links.createViewerAccessToken);

    useEffect(() => {
        // setValidity if all contacts have firstName, lastName, and email
        const contacts = shareNoteState.state.contacts;
        const allValid = contacts.length > 0 && contacts.every(contact => {
            return contact.firstName && contact.lastName &&
                contact.contactMethods?.some(cm => cm.type === 'email' && cm.value && EmailValidator.test(cm.value));
        });
        setValidity(allValid);
    }, [shareNoteState.state.contacts]); // eslint-disable-line react-hooks/exhaustive-deps

    /* useEffect(() => {
        if (note) {
            tracker.onNoteViewed(note, trackingFrom);
        }
    }, [note]); // eslint-disable-line react-hooks/exhaustive-deps */

    useEffect(() => {
        shareNoteState.setState({ note });
    }, [note]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setMaxHouseholdMembersMet(shareNoteState.getTotalHouseholdMemberCount() === MAX_HOUSEHOLD_MEMBERS);
    }, [shareNoteState.state.contacts]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!note) {
        return;
    }

    const onAdd = () => {
        if (isPlatform('capacitor')) {
            setShowAddContactPopover(true);
        } else {
            manuallyAddContact();
        }
    };

    const fromContacts = () => {
        addFromContactsRef.current?.click();
    };

    const manuallyAddContact = () => {
        shareNoteState.addContact({
            firstName: '',
            lastName: '',
            contactMethods: [{ type: 'email', value: '' }]
        });

        focusFirstName();
        scrollIntoView(lastRowRef);
    };

    return (
        <div>
            <div className={classes.upperSection}>
                <Text type="title">Who should be kept in the loop?</Text>
                <Text type="subdetail">
                    Invite others to your Innerhive community to keep them them in the loop! Add close family members, those who live with {careMapState.getPatientFirstName()}, or anyone else that helps with daily care to the Inner Circle to keep their contact details more accessible in {careMapState.getPatientFirstName()}'s care map.
                </Text>
                <IonButton id="add-sharee-btn"
                    data-type="add-sharee-btn"
                    onClick={onAdd}
                >
                    + Add Contact
                </IonButton>
            </div>

            {isPlatform('capacitor') && (
                <>
                    <AddFromPopover
                        trigger="add-sharee-btn"
                        isOpen={showAddContactPopover}
                        onDidDismiss={() => setShowAddContactPopover(false)}
                        buttonHandlers={{
                            manually: manuallyAddContact,
                            fromContacts: fromContacts
                        }}
                    />

                    <div style={{display: 'none'}}>
                        <AddFromContactsButton
                            btnRef={addFromContactsRef}
                            onContactSelected={((patch: IAddFromContactsButtonResult) => {
                                delete patch.organization;

                                patch.contactMethods = patch.contactMethods.map(cm => {
                                    if (cm.type === 'mobile-phone' || cm.type === 'work-phone') {
                                        cm.value = formatPhoneNumber(cm.value);
                                    }
                                    return cm;
                                });

                                shareNoteState.addContact(patch);
                                scrollIntoView(lastRowRef);
                            })}
                        />
                    </div>
                </>
            )}

            {shareNoteState.state.contacts.map(contact => (
                <div key={contact.id} style={{paddingTop: 24}}>
                    <div style={{display: 'flex', flexDirection: 'column', gap: 20, flexGrow: 1}}>
                        <TextField
                            required
                            name="first-name"
                            label="First Name"
                            autocapitalize="words"
                            inputRef={firstNameRef}
                            value={contact.firstName}
                            onInput={firstName => shareNoteState.updateContact(contact.id, { ...contact, firstName })}
                        />

                        <TextField
                            required
                            name="last-name"
                            label="Last Name"
                            autocapitalize="words"
                            value={contact.lastName}
                            onInput={lastName => shareNoteState.updateContact(contact.id, { ...contact, lastName })}
                        />

                        <EmailField
                            required
                            method={careMapState.getContactMethodOrDefault(contact, 'email') as IEmailContactMethod}
                            methodChanged={(method: IContactMethod) => {
                                shareNoteState.updateContact(contact.id, { ...contact, contactMethods: [method] });
                            }}
                        />

                        <div
                            ref={lastRowRef}
                            style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                        >
                            {maxHouseholdMembersMet && !contact.addToHousehold ? (
                                <Text>
                                    You can only add {MAX_HOUSEHOLD_MEMBERS} members to your Inner Circle.
                                </Text>
                            ) : (
                                <div style={{paddingLeft: 2, fontSize: 14}}>
                                    <Checkbox
                                        label="Add to Inner Circle"
                                        checked={contact.addToHousehold || false}
                                        onChange={checked => shareNoteState.updateContact(contact.id, { ...contact, addToHousehold: checked })}
                                    />
                                </div>
                            )}
                            <IonButton
                                fill="clear"
                                size="default"
                                onClick={() => shareNoteState.removeContact(contact.id!)}
                            >Remove Contact</IonButton>
                        </div>
                    </div>
                </div>
            ))}

            {isPlatform('capacitor') && hasCreateViewerAccessTokenLink && (
                <div style={{paddingTop: 56, textAlign: 'center'}}>
                    <Text type="subdetail">
                        Want to share with someone who doesn't have an email address? Simply <div style={{display: 'inline-block', marginTop: -12}}>&nbsp;<ViewShareLinkButton trackingFrom="Sharing Set Up" asLink hasDuration={false} buttonLabelWhenNativeShare="send them a link!" duration={30} /></div>
                    </Text>
                </div>
            )}
        </div>
    );
};

export const ShareNoteDrawerView: FC<IShareNoteDrawerProps> = ({ noteId }) => {
    const alert = useInstance(AlertPresenter);
    const history = useInstance(HistoryViewModel);
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);
    const careMapState = useInstance(CareMapState);
    const tracking = useInstance(MixPanelEventEmitter);

    const shareNoteState = useInstance(ShareNoteState);
    useSubscription(() => shareNoteState);

    const [isValid, setIsValid] = useState(false);
    const [saving, setSaving] = useState(false);

    const saveAndClose = async () => {
        setSaving(true);
        try {
            // copy shareNoteState contacts to a new array to avoid mutation
            const contacts = shareNoteState.state.contacts.map(c => ({ ...c }));

            await shareNoteState.shareWithContacts();
            drawerHelper.closeAndRedirect('/community-redirect');

            if (!shareNoteState.hasShared()) {
                alert.showAlertV2({
                    header: 'Way to go!',
                    message: `Your note has been sent to your invited community members. You'll be notified of any comments or reactions they add to your post here in the community feed. Your Inner Circle members have also been added to ${careMapState.getPatientFirstName()}'s care map!`
                });
            }

            tracking.track('Share Set Up Completed', () => (
                {
                    'Total Contacts': contacts.length,
                    'Inner Circle Members Added': contacts.filter(c => c.addToHousehold).length
                }
            ));

            tracking.track('Note Posted to Community', () => (
                { 'Method': 'Share Set-Up' }
            ));
        }
        catch (err: any){
            alert.showAndLogError(err);
            setSaving(false);
        }
    };

    return (
        <QueryParamDrivenDrawerPage
            title="Set Up Sharing"
            data-type="share-note-drawer"
            onClose={drawerHelper.close}
            buttons={
                <>
                    <IonButton
                        data-type="save-button"
                        disabled={!isValid || saving}
                        onClick={saveAndClose}
                    >
                        {saving ? <IonSpinner name="dots" /> : 'Share'}
                    </IonButton>
                    <IonButton data-type="cancel-button" fill="outline" onClick={() => {
                        history.back();
                    }}>Cancel</IonButton>
                </>
            }
        >
            <BlockUntilActiveCaremap>
                <ShareNoteDrawerContent
                    noteId={noteId}
                    setValidity={(valid: boolean) => setIsValid(valid)}
                />
            </BlockUntilActiveCaremap>
        </QueryParamDrivenDrawerPage>
    );
};
